import { apiClient } from "@/lib/api-client";
/**
 * Plan 工作台数据层 · /admin/v1/plan-lab/*
 * 刀 0：从 Loop 数据层原样拷贝刀 0 页面用得到的请求与出入参类型（D-20 完全隔离：拷一份作 plan 本地类型，
 * 不 import、不引用 Loop 的任何类型），只改接口前缀与缓存键；来源登记见 components/plan-lab/COPIED_FROM.json。
 * PR0 时后端只有元信息接口（A-01 /meta），其余请求返回 404，页面走空态 ／ 失败态。
 */
import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useMemo } from "react";

export type TraceStep = {
	decide: string;
	skill: string;
	observe: string;
	params?: Record<string, unknown>;
	reason?: string; // 主 Agent 本轮为何这么决策(过程还原)
};
/** 节点级执行还原:N2/N3(LLM)+ 规则 + 工具(完整 I/O)。 */
export type NodeEvent = {
	seq: number;
	round: number;
	node: string; // n1_intent / n2_decide / n3_generate / n1_resume / B2.5 / G1 / G4 / G6 / offline / skill 名
	kind: "llm" | "rule" | "tool" | "note";
	title: string;
	ms: number; // 该环节实测耗时(毫秒)
	input: Record<string, unknown>;
	output: Record<string, unknown>;
	// LLM 节点该次调用的 token 用量(输入/输出/合计);非 LLM 节点无此字段
	usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
};
/** 结构化输出:有序块(每块自带 kind,前端逐块渲染;卡片数据来自 Skill 真实返回)*/
export type TextBlock = { kind: "text"; text: string };
/** price_text：后端按商品币种写好的价格（如 $45.5，业务包 card_text.currency_symbols，10-28 T 组）；旧记录没有就只显示数字 */
export type ProductCard = {
	kind: "product";
	productId: string;
	title: string;
	price: number;
	currency?: string;
	price_text?: string;
};
export type CouponCard = {
	kind: "coupon";
	code: string;
	type?: string;
	amount?: number;
	amount_text?: string;
	desc?: string;
};
export type ImageBlock = { kind: "image"; url: string };
export type OutputBlock = TextBlock | ProductCard | CouponCard | ImageBlock;
/** 轮级耗时分桶 + token 分桶(输入/输出/合计,按节点 usage 求和) */
export type Timing = {
	total_ms?: number;
	llm_ms?: number;
	tool_ms?: number;
	rule_ms?: number;
	llm_calls?: number;
	tokens_in?: number; // 输入 token 合计
	tokens_out?: number; // 输出 token 合计
	tokens_total?: number; // 输入+输出 合计(每次运行消耗)
};
export type TurnView = {
	msg: string;
	resume: string;
	intent: string | null;
	calls: string[];
	terminal: string | null;
	stop: string;
	clarify: string | null;
	committed: boolean;
	reply: string;
	trace: TraceStep[];
	node_trace?: NodeEvent[];
	timing?: Timing;
	blocks?: OutputBlock[];
};
// ---- 统一场景库(E3)----
export type ScenarioTurnSpec = {
	msg: string;
	resume: string;
	expect: Record<string, unknown>;
};
export type Scenario = {
	id: string;
	desc: string;
	kind: "single" | "mt_offline" | "mt_llm";
	type: string; // 售前/售中/售后/其他
	intent: string | null;
	customer_id: string;
	multi: boolean;
	modes: ("offline" | "llm")[];
	golden_skills: string[];
	turns: ScenarioTurnSpec[];
};
export type SkillInputField = { name: string; required: boolean; note: string };
export type SkillRow = {
	name: string;
	// desc 现在直接是注册表的 n2_desc(人机同一句)——此前另存一份 io_desc,9/14 已漂
	desc: string;
	// READ / WRITE / GENERIC:WRITE 蕴含 G5 确认门。此前后端一直在传、前端从不展示,
	// 「写操作·确认门」这个信号只活在 io_desc 的散文里 —— 删双源就得把它接出来
	tool_type: string;
	logic: string;
	required: string[];
	input: SkillInputField[];
	sample_input: Record<string, unknown>;
	sample_output: Record<string, unknown>;
};
export type ParamSource = { code: string; name: string; note: string };
export type SeedCustomer = {
	customer_id: string;
	name: string;
	email: string;
	bg: string;
	order_count: number;
};
export type SeedOrder = {
	orderId: string;
	customer_id: string | null;
	status: string | null;
	fulfillment_status: string | null;
	created_at: string | null;
	items: string;
};
export type SeedVariant = {
	variantId: string;
	options: Record<string, unknown>;
	price: number;
	inventory: number;
};
export type SeedProduct = {
	productId: string;
	title: string;
	discontinued: boolean;
	variants: SeedVariant[];
};
export type SeedView = {
	customers: SeedCustomer[];
	orders: SeedOrder[];
	products: SeedProduct[];
};
export type CatalogResponse = {
	skills: SkillRow[];
	seed: SeedView;
	return_reasons: string[];
	param_sources: ParamSource[];
};
export function useCatalog() {
	return useQuery<CatalogResponse>({
		queryKey: ["plan-lab", "catalog"],
		queryFn: async () => {
			const { data } = await apiClient.get<CatalogResponse>("/admin/v1/plan-lab/catalog");
			return data;
		},
	});
}
// ---- C5 评测(机器顾客 × 多维判分 × pass^k)----
export type SimPersona = { name: string; desc: string };
export type SimScenarioEval = {
	expect_skills?: string[];
	forbid_skills?: string[];
	communicate_info?: string[];
	nl_assertions?: string[];
	reward_basis?: string[];
	// 指南契约(刀D M4:场景库覆盖率统计用)
	guidance_assertions?: Array<{ guideline_id: string; assertion: string; polarity: string }>;
	guidance_recall_expected?: string[];
};
/**
 * 题目「已知问题」标注：题库里的原文全是 Loop 时期结论，后端（eval/known_issues.py）逐题处理后给页面——
 * Plan 上不适用或已补上的不给；其余带出处 `origin`：loop＝Loop 时期结论（Plan 上未复核）、design＝负例设计。
 */
export type SimKnownIssue = {
	cat: string;
	note: string;
	ref?: string | null;
	origin: "loop" | "design";
	plan_note: string;
};
/**
 * 一道题。盲测集 blind、留出集 holdout* 的题 `sealed: true`：接口只给题号、题族、阶段、意图、难度、所属测试集、
 * 金标状态，题面（来意、任务说明、判分断言等）和金标都不给（D-05）。
 */
export type SimScenario = {
	id: string;
	sealed?: boolean;
	desc?: string;
	stage: string; // 售前/售中/售后/其他
	intent: string;
	difficulty: string; // 缺信息/多候选/已发货不可/越权诱导/红线/顺畅
	category?: string | null; // 品类(tau2 搬入题:数码3C/家居/运动/多品类…;鞋服题为空)
	known_issue?: SimKnownIssue | null;
	persona?: string | null;
	customer_id?: string;
	data_refs?: { orders: string[]; products: string[] };
	reason_for_call?: string;
	known_info?: string;
	unknown_info?: string;
	task_instructions?: string;
	eval?: SimScenarioEval;
	/** PR7 起题目取自题库快照，每题多带这几项（PR9 页面用） */
	family?: string | null;
	fingerprint?: string | null;
	has_gold?: boolean;
	gold_status?: string | null;
	multi_demand?: boolean;
	required_trials?: number;
	/** 所属测试集（批 3 起） */
	test_sets?: string[];
};
export type SimScenariosResponse = {
	has_key: boolean;
	bank_id?: string | null;
	/** 题库快照的派生来源与这一版的改动说明（详情在题库快照页） */
	derived_from?: string | null;
	derive_note?: string | null;
	personas: SimPersona[];
	scenarios: SimScenario[];
};
export type TestSetInfo = {
	name: string;
	desc?: string;
	n: number;
	ids: string[];
	defaults: Record<string, boolean>;
	/** 盲测 ／ 留出集：题号公开、题面不公开 */
	sealed?: boolean;
	/** 退役集（判分规则 EV-26）：不能再发起批次；历史批次与报告照常可看 */
	retired?: { retired_at?: string; reason?: string; replaced_by?: string | null } | null;
};
export function useTestSets() {
	return useQuery<{ bank_id?: string | null; sets: TestSetInfo[] }>({
		queryKey: ["plan-lab", "test-sets"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ bank_id?: string | null; sets: TestSetInfo[] }>(
				"/admin/v1/plan-lab/sim/test-sets",
			);
			return data;
		},
	});
}
export function useSimScenarios() {
	return useQuery<SimScenariosResponse>({
		queryKey: ["plan-lab", "sim-scenarios"],
		queryFn: async () => {
			const { data } = await apiClient.get<SimScenariosResponse>(
				"/admin/v1/plan-lab/sim/scenarios",
			);
			return data;
		},
	});
}
// ---- SkillPort:技能执行 backend(V2-P1) ----
export type SkillPortSkill = {
	name: string;
	type: string;
	backend: string;
	live_domain: string;
	recorded_count: number;
	latest_recording: {
		params: Record<string, unknown>;
		res: Record<string, unknown>;
		ts?: string;
	} | null;
};
export type SkillPortProbe = {
	ts: string | null;
	ok: boolean;
	by_domain: Record<string, { ok: boolean; error_code: string }>;
};
export type SkillPortConfig = {
	config: { default: string; skills: Record<string, string>; replay_miss: string };
	backends: string[];
	miss_policies: string[];
	skills: SkillPortSkill[];
	recordings: { total: number; by_skill: Record<string, number>; cap: number };
	probe: SkillPortProbe | null;
	merchant: ConnectMerchant;
	last_change: { ts: string; before: unknown; after: unknown } | null;
	snapshot: { default: string; skills: Record<string, string>; non_fixture: string[] };
};
export function useSkillPort() {
	return useQuery<SkillPortConfig>({
		queryKey: ["plan-lab", "skillport"],
		queryFn: async () => {
			const { data } = await apiClient.get<SkillPortConfig>("/admin/v1/plan-lab/skillport");
			return data;
		},
	});
}
// ---- connect:Shopify 连接层(V2-P1 PR-3) ----
export type ConnectMerchant = {
	label: string;
	shop_domain: string;
	api_version: string;
	auth_mode: string;
	enabled: boolean;
	token_configured: boolean;
};

/* ====================== PR0 新写：A-01 元信息（熔断探测） ====================== */

/** A-01 `GET /meta` 出参（PR0 只保证 enabled、code_version 有值，其余为空 ／ 默认，后续 PR 补齐） */
export type PlanMeta = {
	enabled: boolean;
	code_version: string;
	active_snapshot_id: string | null;
	data_schema_version: number | null;
	llm_key_configured: boolean | null;
	tool_backend: string | null;
	ignored_config_keys: string[];
	/** 诉求显示名（标签字典批 1：只读意图表 name 一处派生，含表外键兜底） */
	demand_labels?: Record<string, string>;
};

/** 外壳启动时调一次；404 = 熔断（PLAN_LAB_ENABLED=false） */
export function usePlanMeta() {
	return useQuery<PlanMeta>({
		queryKey: ["plan-lab", "meta"],
		queryFn: async () => {
			const { data } = await apiClient.get<PlanMeta>("/admin/v1/plan-lab/meta");
			return data;
		},
		retry: false,
		staleTime: 5 * 60_000,
	});
}

/** 只有元信息接口明确回 404（熔断）才隐藏 Plan 段；加载中、其他失败照常显示（判定卡 01 状态清单） */
export function usePlanEnabled(): boolean {
	const meta = usePlanMeta();
	if (isAxiosError(meta.error) && meta.error.response?.status === 404) return false;
	return meta.data?.enabled !== false;
}

/* ====================== PR3 新写：人工提问 ／ 运行记录接 plan 后端 ====================== */
/* 形状以接口设计 v0.3 为准：A-11 会话一轮（E-01）、A-21 运行记录列表（I-18）、
 * A-22 运行记录详情（批 1 子集）、清空、目录。 */

/** 出错体 `{detail: {error_code, message, hint, extra}}` → 一句给人看的话 */
export function planErrorMessage(e: unknown): string {
	if (isAxiosError(e)) {
		const d = e.response?.data?.detail as
			| { error_code?: string; message?: string; hint?: string | null }
			| string
			| undefined;
		if (d && typeof d === "object" && d.message) {
			return d.hint ? `${d.message}（${d.hint}）` : d.message;
		}
		if (e.response?.status) return `请求失败（HTTP ${e.response.status}）`;
		return "连不上后端，请稍后重试";
	}
	return e instanceof Error ? e.message : String(e);
}

/** 出错体里的 error_code（拿不到为 null） */
export function planErrorCode(e: unknown): string | null {
	if (!isAxiosError(e)) return null;
	const d = e.response?.data?.detail as { error_code?: string } | undefined;
	return d && typeof d === "object" ? (d.error_code ?? null) : null;
}

export type PlanActivation = {
	mode: "first_turn" | "turn_n" | "on_mention" | string;
	turn?: number | null;
	group?: string | null;
	anchor?: string | null;
};
/** E-01 plan_view.items[] */
export type PlanItemView = {
	item_id: string;
	demand_type: string;
	object: Record<string, unknown> | null;
	status: string;
	status_reason: Record<string, unknown> | null;
	wait_kind: string | null;
	speakable: string | null;
	markers: string[];
	evidence_brief: (string | null)[];
	active: boolean;
	activation: PlanActivation | null;
	activated_turn: number | null;
	claim: string | null;
};
export type PlanView = {
	plan_version: number | null;
	items: PlanItemView[];
	asked_item_id: string | null;
};
export type PlanToolCall = {
	tool_id: string;
	args: Record<string, unknown>;
	result_brief: Record<string, unknown>;
	status: string;
	error_code: string | null;
	latency_ms: number;
	item_id: string | null;
	is_write: boolean;
	write_class: string | null;
};
export type PlanInjectCheck = {
	rule: string;
	item_id: string | null;
	verdict: string;
	detail: string;
};
/** E-01 一轮输出 */
export type PlanTurnView = {
	reply: string;
	blocks: OutputBlock[];
	latency_ms: number;
	tool_calls: PlanToolCall[];
	plan_view: PlanView;
	trace_ref: {
		record_id: string;
		trace_id: string;
		session_id: string;
		snapshot_id: string | null;
		code_version: string | null;
	};
	resume_action: string | null;
	new_item_ids: string[];
	stop_reason: string;
	conservative: boolean;
	claim_check: { verdict: string; mismatches: Record<string, unknown>[] };
};
export type PlanTurnResponse = {
	state: Record<string, unknown>;
	turn: PlanTurnView;
	record_id: string;
};

/** A-11 会话一轮 */
export function usePlanTurn() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: {
			state: Record<string, unknown> | null;
			message: string;
			customer_id?: string;
		}) => {
			const { data } = await apiClient.post<PlanTurnResponse>(
				"/admin/v1/plan-lab/session/turn",
				body,
				{ timeout: 120_000 },
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["plan-lab", "records"] }),
	});
}

/** I-18 运行记录行（PR3 起带 customer_id、calls） */
export type PlanRecordRow = {
	record_id: string;
	session_id: string;
	turn_no: number | null;
	source: "ask" | "eval" | "bridge" | string;
	batch_id: string | null;
	trial_id: string | null;
	scenario_id: string | null;
	customer_id?: string | null;
	plan_source: string;
	message: string;
	reply_brief: string;
	item_counts: Record<string, number> | null;
	calls?: string[];
	stop_reason: string | null;
	plan_version: number | null;
	guard_hits: number;
	write_object_blocked: number;
	false_claim: number;
	llm_calls: number;
	tokens_in: number;
	tokens_out: number;
	cost_usd: number;
	latency_ms: number;
	error_type: string | null;
	mode: string;
	snapshot_id: string | null;
	code_version: string | null;
	created_at: string;
	/** 盲测会话（10-21 A1）：客户原话 ／ 回复 ／ 工具 ／ 事项已在接口层清空，页面写「盲测题，内容不展示」 */
	sealed?: boolean;
};
export type PlanRecordsPage = { rows: PlanRecordRow[]; next_cursor: string | null; total: number };

/** 列表一次最多拉的条数（A-21 每页 ≤ 200，按 cursor 连拉；页面筛选与翻页在前端做） */
export const PLAN_RECORDS_MAX = 1000;

/** A-21 运行记录列表：先渲染第一批（200 条），其余按 cursor 在后台接着拉（最多 PLAN_RECORDS_MAX 条），最新在前。
 * 之前是连拉到底才出表，记录一多页面长时间停在「加载中」。 */
export function usePlanRecords() {
	const q = useInfiniteQuery<
		PlanRecordsPage,
		Error,
		InfiniteData<PlanRecordsPage>,
		string[],
		string | null
	>({
		queryKey: ["plan-lab", "records"],
		retry: false,
		initialPageParam: null,
		queryFn: async ({ pageParam }) => {
			const params: Record<string, string | number> = { limit: 200 };
			if (pageParam) params.cursor = pageParam;
			const { data } = await apiClient.get<PlanRecordsPage>("/admin/v1/plan-lab/records", {
				params,
			});
			return data;
		},
		getNextPageParam: (last) => last.next_cursor ?? undefined,
	});
	const { hasNextPage, isFetchingNextPage, fetchNextPage, isError: failed } = q;
	const loaded = q.data?.pages.reduce((n, p) => n + p.rows.length, 0) ?? 0;
	useEffect(() => {
		if (hasNextPage && !isFetchingNextPage && !failed && loaded < PLAN_RECORDS_MAX)
			void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, failed, loaded, fetchNextPage]);
	const data = useMemo(
		() =>
			q.data
				? {
						rows: q.data.pages.flatMap((p) => p.rows).slice(0, PLAN_RECORDS_MAX),
						total: q.data.pages[0]?.total ?? 0,
					}
				: undefined,
		[q.data],
	);
	return {
		data,
		isLoading: q.isLoading,
		isError: q.isError,
		error: q.error,
		isFetching: q.isFetching,
		refetch: q.refetch,
	};
}

/** I-16 留痕事件（骨架键是带点的 OTel 名） */
export type PlanEvent = {
	span_id: string;
	parent_span_id: string | null;
	trace_id: string;
	"gen_ai.operation.name": string;
	"plan.layer": string;
	"plan.item_id": string | null;
	start_time: string;
	end_time: string;
	"error.type"?: string | null;
	"gen_ai.usage.input_tokens"?: number;
	"gen_ai.usage.output_tokens"?: number;
	"gen_ai.response.model"?: string | null;
	"gen_ai.request.model"?: string | null;
	"gen_ai.provider.name"?: string | null;
	"gen_ai.conversation.id"?: string | null;
	"plan.version"?: number | null;
	"plan.snapshot_id"?: string | null;
	"plan.cost_usd"?: number | null;
	"plan.cost_estimated"?: boolean | null;
	attributes: Record<string, unknown>;
	/** PR4 起 A-22 的树只带摘要（长文本截到 200 字），完整的走 A-25 */
	attributes_summarized?: boolean;
};
export type PlanRecordDetail = {
	record: PlanRecordRow;
	header: {
		session_id: string | null;
		title: string;
		mode: string;
		source: string | null;
		customer_id: string | null;
		snapshot_id: string | null;
		plan_version: number | null;
		turn_no: number | null;
	};
	dialogue: {
		record_id: string;
		turn_no: number | null;
		message: string;
		reply: string;
		stop_reason: string | null;
	}[];
	tree: PlanEvent[];
	items_now: PlanItemView[];
	asked_item_id: string | null;
	used_in_answer: { available: boolean; note?: string };
	/** 盲测会话（10-21 A1）：对话、事件树、分组、事项都不给 */
	sealed?: boolean;
	/** A-22 批 2（PR4）：按层顺序固定的分组；批 1 记录没有，前端按事件的层 ／ 事项字段自己分 */
	groups?: PlanTraceGroup[];
	/** A-22 批 2（PR4）：本轮按层的模型调用数 ／ token ／ 花费 ／ 耗时（只数模型调用） */
	cost_by_layer?: PlanLayerCost[];
};

/** A-22 `groups[]`（PR4）：`key` ＝ signal ／ recall ／ planning ／ item:<事项编号> ／ execution ／ generation ／ cost */
export type PlanTraceGroup = {
	key?: string;
	layer: string;
	item_id?: string | null;
	title?: string;
	summary?: string;
	collapsed_summary?: string;
	span_ids: string[];
	group_span_id?: string | null;
	available?: boolean;
	note?: string | null;
};
/** A-22 `cost_by_layer[]`（PR4） */
export type PlanLayerCost = {
	layer: string;
	llm_calls?: number;
	input_tokens?: number;
	output_tokens?: number;
	cost_usd?: number;
	cost_estimated_calls?: number;
	latency_ms?: number;
	providers?: string[];
};

/** A-22：PR5 起要树摘要（`tree=summary`，长文本 ≤ 200 字），完整输入输出点开时走 A-25；
 * 不认这个参数的旧后端照样回完整树，前端两种都能显示 */
export async function fetchPlanRecordDetail(recordId: string): Promise<PlanRecordDetail> {
	const { data } = await apiClient.get<PlanRecordDetail>(
		`/admin/v1/plan-lab/records/${encodeURIComponent(recordId)}`,
		{ params: { tree: "summary" } },
	);
	return data;
}

/** A-22（批 1 子集）运行记录详情 */
export function usePlanRecordDetail(recordId: string | null) {
	return useQuery<PlanRecordDetail>({
		queryKey: ["plan-lab", "record-detail", recordId],
		enabled: !!recordId,
		retry: false,
		queryFn: () => fetchPlanRecordDetail(recordId ?? ""),
	});
}

/** 清空人工提问的运行记录（只打标记，留痕 ／ 账本不动） */
export function useClearPlanRecords() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.delete<{ cleared: boolean; cleared_before: string }>(
				"/admin/v1/plan-lab/records",
				{ params: { source: "ask" } },
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["plan-lab", "records"] }),
	});
}

/* ====================== PR5 新写：运行轨迹抽屉（A-22 批 2、A-24、A-25） ====================== */

/** A-25 `GET /events/{span_id}`：一个事件的完整 attributes（A-22 的树只带摘要）。
 * 后端还没有这个接口（PR4 之前）时回 404，调用方退回用树里的 attributes。 */
export function usePlanEvent(spanId: string | null) {
	return useQuery<Record<string, unknown>>({
		queryKey: ["plan-lab", "event", spanId],
		enabled: !!spanId,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: async () => {
			const { data } = await apiClient.get<Record<string, unknown>>(
				`/admin/v1/plan-lab/events/${encodeURIComponent(spanId ?? "")}`,
			);
			// PR4 出参 `{event}`（事件全量）；也认直接给事件 ／ 只给 attributes 的形状
			const ev = ((data as { event?: unknown }).event ?? data) as { attributes?: unknown };
			const attrs = ev.attributes;
			return attrs && typeof attrs === "object"
				? (attrs as Record<string, unknown>)
				: (ev as Record<string, unknown>);
		},
	});
}

/** I-07 计划版本记录（A-24 出参 versions[]） */
export type PlanVersionRecord = {
	version: number;
	prev_version?: number | null;
	generator?: string | null;
	trigger?: string | null;
	turn_id?: string | null;
	snapshot_id?: string | null;
	changes?: Record<string, unknown>[];
	checks?: PlanInjectCheck[];
	raw_output?: unknown;
	items?: Record<string, unknown>[];
	derived?: boolean;
};
export type PlanVersionDiff = { item_id: string; change: string; fields?: string[] };
export type PlanVersionsResponse = { versions: PlanVersionRecord[]; diff?: PlanVersionDiff[] };

/** A-24 会话的计划版本链（不传 a ／ b ＝ 全链） */
export function usePlanVersions(sessionId: string | null) {
	return useQuery<PlanVersionsResponse>({
		queryKey: ["plan-lab", "plan-versions", sessionId],
		enabled: !!sessionId,
		retry: false,
		staleTime: 60_000,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanVersionsResponse>(
				`/admin/v1/plan-lab/sessions/${encodeURIComponent(sessionId ?? "")}/plan-versions`,
			);
			return data;
		},
	});
}

/** 接口回 404（这个接口还没上线 ／ 这条不存在） */
export function isNotFound(e: unknown): boolean {
	return isAxiosError(e) && e.response?.status === 404;
}

/* ====================== PR6 新写：配置（提示词与模型 ／ 配置版本）与评测记录 ====================== */
/* 形状以 PR4 落地记录 §1.5 为准：A-31 读、A-32 保存（＝新快照）、A-33 列表、A-34 单个快照、A-35 对比；A-52 批次列表。 */

export type PlanConfigSchemaItem = {
	key: string;
	label: string;
	type: string;
	is_definition: boolean;
	options: string[];
};
export type PlanModelOption = { key: string; label: string };
/** A-31 `GET /config`：当前生效快照号 ＋ 全部配置项 ＋ 结构 ＋ 候选模型 */
export type PlanConfig = {
	snapshot_id: string | null;
	content: Record<string, unknown>;
	schema: PlanConfigSchemaItem[];
	model_options: PlanModelOption[];
	/** 当前代码随发的默认配置（页面「默认」「重置为默认」读它，不读历史快照） */
	defaults?: Record<string, unknown>;
	editable_sections: string[];
};

/** 不随窗口聚焦自动重取：页面上有草稿时，底稿不能悄悄换掉（冲突要靠快照号判） */
export function usePlanConfig() {
	return useQuery<PlanConfig>({
		queryKey: ["plan-lab", "config"],
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanConfig>("/admin/v1/plan-lab/config");
			return data;
		},
	});
}

export type PlanConfigSaveBody = {
	base_snapshot_id: string;
	changes: Record<string, unknown>;
	note?: string;
	operator: "admin-ui";
};
/** A-32 出参；内容和当前完全相同时 `unchanged = true`、快照号不变 */
export type PlanConfigSaveResult = {
	snapshot_id: string;
	prev_snapshot_id: string | null;
	changed_keys: string[];
	unchanged: boolean;
	seq: number | null;
	effective_from: string;
};

/** A-32 `PUT /config`；出错体 409 snapshot_conflict ／ 400 note_required ／ 400 config_invalid ／ 400 bad_request */
export function useSaveConfig() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: PlanConfigSaveBody) => {
			const { data } = await apiClient.put<PlanConfigSaveResult>("/admin/v1/plan-lab/config", body);
			return data;
		},
		onSuccess: (d) => {
			if (d.unchanged) return;
			qc.invalidateQueries({ queryKey: ["plan-lab", "config-snapshots"] });
			qc.invalidateQueries({ queryKey: ["plan-lab", "meta"] });
		},
	});
}

/** 出错体里的 extra（拿不到为空对象） */
export function planErrorExtra(e: unknown): Record<string, unknown> {
	if (!isAxiosError(e)) return {};
	const d = e.response?.data?.detail as { extra?: Record<string, unknown> } | undefined;
	return d && typeof d === "object" && d.extra && typeof d.extra === "object" ? d.extra : {};
}

/** A-33 一行（每次保存一行，新 → 旧；种子 ／ 种子升级的操作者是 system:seed） */
export type PlanSnapshotRow = {
	snapshot_id: string;
	seq: number | null;
	prev_snapshot_id: string | null;
	changed_keys: string[];
	changed_labels: string[];
	operator: string | null;
	at: string | null;
	note: string;
	is_active: boolean;
	restored_from: string | null;
	/** 保存时的标签字典版本（标签体系 v2 批 5 起记；之前的快照为空） */
	tag_dict_version?: string | null;
};
export type PlanSnapshotsResponse = {
	rows: PlanSnapshotRow[];
	active_snapshot_id: string | null;
	total: number;
};
export const PLAN_SEED_OPERATOR = "system:seed";

export function usePlanSnapshots() {
	return useQuery<PlanSnapshotsResponse>({
		queryKey: ["plan-lab", "config-snapshots"],
		retry: false,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanSnapshotsResponse>(
				"/admin/v1/plan-lab/config/snapshots",
			);
			return data;
		},
	});
}

/** 本批 ／ 当前跑在什么环境上（收尾 B1）：画像版本、工具后端、Jev 锁定版本与分档线、业务包指纹 */
export type PlanRunEnv = {
	profile_version?: string | null;
	tool_backend?: string | null;
	tool_backend_skills?: Record<string, string>;
	jev_model?: string | null;
	jev_seen?: string | null;
	yes_at?: number | null;
	no_at?: number | null;
	pack_name?: string | null;
	pack_params?: string | null;
	pack_digest?: string | null;
	assets_digest?: string | null;
	/** 标签字典版本（批 5 起；之前的批次为空） */
	tag_dict_version?: string | null;
	/** 模型提供方（11-02 起：deepseek ／ openrouter；之前的批次为空，当时都走 openrouter） */
	llm_provider?: string | null;
};

/** 配置版本页「运行环境」（收尾 B1）：生效快照里影响运行的环境项 ＋ 运行时实际取到的值 ＋ 业务包指纹 */
export type PlanRunEnvItem = {
	key: string;
	label: string;
	value: unknown;
	/** 生效快照里有这一项；没有＝B1 之前的旧快照，运行按旧口径取 */
	in_snapshot: boolean;
	effective: unknown;
};
export type PlanConfigRunEnv = {
	snapshot_id: string | null;
	items: PlanRunEnvItem[];
	pack: { name?: string | null; params_digest?: string | null; pack_digest?: string | null };
	code_version: string | null;
	jev_seen: string | null;
	assets_digest: string | null;
	/** 当前标签字典版本（批 5） */
	tag_dict_version?: string | null;
	/** 生效快照保存时记下的字典版本；批 5 之前保存的快照为空 */
	snapshot_tag_dict_version?: string | null;
	/** 模型提供方（11-02）：不出 key 的值，只给变量名与是否配了 */
	llm?: PlanLlmProvider | null;
};
export type PlanLlmProvider = {
	provider: string;
	/** snapshot ＝ 配置快照 plan.llm.provider；settings ＝ .env 默认 */
	source: "snapshot" | "settings" | string;
	base_url: string;
	key_env: string;
	key_configured: boolean;
	/** 各节点：平台模型名 → 实际发给提供方的模型名 */
	models: { node: string; model: string; sent_as: string }[];
	model_map?: Record<string, string>;
	price?: {
		currency: string;
		per_million: Record<string, { off: number[]; peak: number[] }>;
		peak_utc: string;
		cny_per_usd: number;
	};
};
export function usePlanRunEnv() {
	return useQuery<PlanConfigRunEnv>({
		queryKey: ["plan-lab", "config-run-env"],
		retry: false,
		queryFn: async () =>
			(await apiClient.get<PlanConfigRunEnv>("/admin/v1/plan-lab/config/run-env")).data,
	});
}

/** A-34 一个快照的全量内容；不存在 404 */
export type PlanSnapshotDetail = {
	snapshot_id: string;
	created_at: string | null;
	content: Record<string, unknown>;
	audit: PlanSnapshotRow | null;
	is_active: boolean;
};
export function usePlanSnapshot(snapshotId: string | null | undefined) {
	return useQuery<PlanSnapshotDetail>({
		queryKey: ["plan-lab", "config-snapshot", snapshotId],
		enabled: !!snapshotId,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY, // 快照内容不变（快照号是内容哈希）
		queryFn: async () => {
			const { data } = await apiClient.get<PlanSnapshotDetail>(
				`/admin/v1/plan-lab/config/snapshots/${encodeURIComponent(snapshotId ?? "")}`,
			);
			return data;
		},
	});
}

/** A-35 两个快照的配置项级差异（b 相对 a）；提示词与事项类型表 `is_long_text = true` */
export type PlanConfigDiffItem = {
	key: string;
	label: string;
	before: unknown;
	after: unknown;
	is_long_text: boolean;
};
export type PlanConfigDiff = { a: string; b: string; changes: PlanConfigDiffItem[] };
export function usePlanConfigDiff(a: string | null | undefined, b: string | null | undefined) {
	return useQuery<PlanConfigDiff>({
		queryKey: ["plan-lab", "config-diff", a, b],
		enabled: !!a && !!b,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanConfigDiff>("/admin/v1/plan-lab/config/diff", {
				params: { a, b },
			});
			return data;
		},
	});
}

/** A-52 批次索引行（接口设计 A-52；批 2 时列表恒空，PR7 起由评测作业写入）。
 * 性格 ／ 平均分 ／ 三维通过率 ／ pass^k 不在 A-52 定稿字段里，按可选读，取不到显示「—」 */
export type PlanBatchRow = {
	batch_id: string;
	created_at: string | null;
	status: string;
	stop_reason?: string | null;
	locked_snapshot_id?: string | null;
	bank_id?: string | null;
	gold_version?: string | null;
	scenario_count?: number | null;
	trial_count?: number | null;
	done?: number | null;
	undetermined?: number | null;
	pass_by_family?: Record<string, number>;
	write_object_errors?: number | null;
	false_claims?: number | null;
	cost_usd?: number | null;
	avg_reward?: number | null;
	dim_pass?: { action?: number | null; communicate?: number | null; nl?: number | null };
	pass_k?: Record<string, number>;
	/** PR9 加：每题次数、题号、作业类别 */
	trials_per_scenario?: number | null;
	scenario_ids?: string[];
	kind?: string | null;
	/** 收尾 B1：画像版本、工具后端、Jev 锁定版本与分档线、业务包指纹 */
	run_env?: PlanRunEnv;
};
export type PlanBatchesResponse = {
	rows: PlanBatchRow[];
	next_cursor: string | null;
	total: number;
};
/** 批次列表一次取一页 200 条（接口上限），前端筛选 ／ 翻页；批次多了再改服务端翻页 */
export function usePlanBatches() {
	return useQuery<PlanBatchesResponse>({
		queryKey: ["plan-lab", "eval-batches"],
		retry: false,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanBatchesResponse>("/admin/v1/plan-lab/eval/batches", {
				params: { limit: 200 },
			});
			return data;
		},
	});
}

/* ====================== PR9 新写：模拟场景（发起 ／ 进度 ／ 报告） ====================== */
/* 形状以接口设计 §3.6～§3.8 与 PR7 ／ PR8 落地记录为准：A-51 发起、A-53 报告、A-54 进度、A-55 停批、A-56 补跑、
 * A-57 单个 trial、A-58 估价。 */

/** 发起 ／ 估价的选题（A-51 ／ A-58 共用） */
export type PlanEvalSelect = {
	bank_id: string;
	scenario_ids?: string[];
	test_set?: string;
	/** 不给 = 按规定次数（多诉求 5、其余 3） */
	trials?: number;
};

/** A-58 估价与余额检查 */
export type PlanEstimate = {
	estimate_usd: number;
	basis: string;
	balance_usd: number | null;
	allowed: boolean;
	blocked_reason: string | null;
	blocked_code: string | null;
	suggest_split: string[][];
	limit_usd: number;
	trials: Record<string, number>;
};
export function usePlanEstimate(body: PlanEvalSelect | null) {
	return useQuery<PlanEstimate>({
		queryKey: ["plan-lab", "eval-estimate", body],
		enabled: !!body,
		retry: false,
		staleTime: 30_000,
		queryFn: async () => {
			const { data } = await apiClient.post<PlanEstimate>("/admin/v1/plan-lab/eval/estimate", body);
			return data;
		},
	});
}

/** A-51 发起（后台作业，立即返回批次号） */
export type PlanBatchCreated = {
	batch_id: string;
	status: string;
	queue_position: number | null;
	locked_snapshot_id: string | null;
	estimate_usd: number | null;
	total_trials: number;
};
export function useCreateBatch() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: PlanEvalSelect & { note?: string }) => {
			const { data } = await apiClient.post<PlanBatchCreated>(
				"/admin/v1/plan-lab/eval/batches",
				body,
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["plan-lab", "eval-batches"] }),
	});
}

/** A-54 批次进度 */
export type PlanBatchProgress = {
	batch_id: string;
	status: string | null;
	stop_reason: string | null;
	total_trials: number;
	done: number;
	undetermined: number;
	running_scenario_id: string | null;
	running_scenario_ids: string[];
	cost_usd_so_far: number;
	cost_limit_usd: number;
	elapsed_s: number | null;
	eta_s: number | null;
	stop_requested: boolean;
	queue_position: number | null;
};
export const BATCH_ACTIVE = new Set(["queued", "running", "stopping"]);
/** 跑着时每 2 秒轮询一次，结束就停 */
export function useBatchProgress(batchId: string | null) {
	return useQuery<PlanBatchProgress>({
		queryKey: ["plan-lab", "eval-progress", batchId],
		enabled: !!batchId,
		retry: false,
		refetchInterval: (q) => (BATCH_ACTIVE.has(q.state.data?.status ?? "") ? 2_000 : false),
		queryFn: async () => {
			const { data } = await apiClient.get<PlanBatchProgress>(
				`/admin/v1/plan-lab/eval/batches/${encodeURIComponent(batchId ?? "")}/progress`,
			);
			return data;
		},
	});
}
function useBatchAction(path: "stop" | "rerun-undetermined") {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (batchId: string) => {
			const { data } = await apiClient.post(
				`/admin/v1/plan-lab/eval/batches/${encodeURIComponent(batchId)}/${path}`,
				{},
			);
			return data as Record<string, unknown>;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["plan-lab"] }),
	});
}
/** A-55 停批（当前在跑的会话跑完才停） */
export const useStopBatch = () => useBatchAction("stop");
/** A-56 补跑未判定 */
export const useRerunBatch = () => useBatchAction("rerun-undetermined");

/** A-53 报告：按题块里一次试跑 */
export type PlanTrialResult = {
	trial_id: string;
	index: number;
	outcome: "pending" | "pass" | "fail" | "undetermined" | string;
	reason: string | null;
	detail: string | null;
	attempt: number | null;
	persona: string | null;
	num_turns: number | null;
	termination_reason: string | null;
	dims: { action: boolean | null; communicate: boolean | null; nl: boolean | null } | null;
	failed_dims: string[];
	false_claims: number | null;
	write_object_errors: number | null;
	/** 违规提交、重复提交（10-25 起重复提交进安全一票否决）；老后端没有这两个键 */
	forbidden_commits?: number | null;
	duplicate_commits?: number | null;
	record_ids: string[];
	cost_usd: number | null;
	/** 盲测题（10-21 A1）：只给结论与聚合数，判分维度、运行记录号都不给 */
	sealed?: boolean;
};
export type PlanReportScenario = {
	scenario_id: string;
	family: string | null;
	/** 盲测题（10-21 A1） */
	sealed?: boolean;
	required: number;
	valid: number;
	stable_class: "stable_pass" | "flaky" | "stable_fail" | "insufficient" | string;
	item_completion: number | null;
	results: PlanTrialResult[];
};
export type PlanFamilyRow = {
	family: string;
	scenarios: number;
	stable_pass: number;
	flaky: number;
	stable_fail: number;
	insufficient: number;
	pass_1: number | null;
	pass_3: number | null;
};
/** A-53 批次报告（固定区块；运行中 partial=true） */
/** 情绪与风险识别汇总（口径只在后端 eval/emotion_risk.py） */
export type PlanEmotionRiskFlag = {
	should: number;
	hit: number;
	recall: number | null;
	should_not: number;
	false: number;
	false_rate: number | null;
};
export type PlanEmotionRiskSummary = {
	trials?: number;
	sentiment?: { judged: number; hit: number; rate: number | null };
	flags?: Record<string, PlanEmotionRiskFlag>;
	session?: { should: number; missed: number; should_not: number; false: number };
	negative?: { trials: number; trials_false: number; false_flags: number };
	unaligned?: number;
	names?: Record<string, string>;
};

export type PlanBatchReport = {
	partial: boolean;
	header: {
		batch_id: string;
		status: string | null;
		stop_reason: string | null;
		locked_snapshot_id: string | null;
		bank_id: string | null;
		gold_version: string | null;
		code_version: string | null;
		created_at: string | null;
		started_at: string | null;
		ended_at: string | null;
		note: string | null;
		kind: string | null;
		/** 收尾 B1：本批跑在什么环境上（建批时记下的运行资产） */
		run_env?: PlanRunEnv;
	};
	credibility: {
		judge_calibration: string;
		undetermined: number;
		undetermined_reasons: Record<string, number>;
		insufficient_scenarios: { scenario_id: string; valid: number; required: number }[];
		write_basis?: string;
		[k: string]: unknown;
	};
	overview: {
		pass_rate: number | null;
		/** 不扣安全否决的旧口径，只作对照（基线升级 R2） */
		reward_rate?: number | null;
		/** 观测列（基线升级 R1）：指南断言判定，不进通过率 */
		guidance?: { assertions?: number; met?: number; trials_violated?: number; errors?: number };
		/** 观测列（基线升级 R11）：编造事实，不进通过率 */
		fabrication?: { trials?: number; violated?: number; errors?: number };
		/** 观测列（10-22）：情绪与风险识别，只比对信号每轮输出与金标期望，不进通过率；trials 为 0 ／ 老批次没有 → 未判分 */
		emotion_risk?: PlanEmotionRiskSummary;
		/** 合规断言逐条（EV-23）：判了几条、满足几条、不适用几条（不适用不进分母）；老后端没有 */
		nl_assertions?: { judged?: number; met?: number; not_applicable?: number };
		/** 确定性检查（EV-25，10-25 起执行）：机判 ／ 没过 ／ 未机判条数；老批次没执行 */
		deterministic?: { judged?: number; failed?: number; unparsed?: number };
		n: number;
		dim_pass: { action?: number | null; communicate?: number | null; nl?: number | null };
		pass_k: Record<string, number>;
		undetermined: number;
		pending: number;
		scenario_count: number;
		insufficient_count: number;
	};
	by_family: PlanFamilyRow[];
	safety: {
		write_object_errors: number;
		write_param_mismatch: number | null;
		false_claims: number;
		forbidden_commits: number;
		/** 重复提交（EV-21）：同工具同对象第二次及以后的提交 */
		duplicate_commits?: number;
	};
	cost: { cost_usd: number | null; estimate_usd: number | null; limit_usd: number | null };
	scenarios: PlanReportScenario[];
	reruns: { at: string; trial_ids: string[]; estimate_usd: number }[];
};
export function usePlanBatchReport(batchId: string | null, live = false) {
	return useQuery<PlanBatchReport>({
		queryKey: ["plan-lab", "eval-batch", batchId],
		enabled: !!batchId,
		retry: false,
		refetchInterval: live ? 4_000 : false,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanBatchReport>(
				`/admin/v1/plan-lab/eval/batches/${encodeURIComponent(batchId ?? "")}`,
			);
			return data;
		},
	});
}

/** A-57 单个 trial：评分卡 ＋ 会话 ＋ 记录号 */
export type PlanTrialDetail = {
	trial_id: string;
	batch_id: string;
	scenario_id: string;
	/** 盲测题（10-21 A1）：对话、评分卡、金标事项、运行记录号都已清空 */
	sealed?: boolean;
	outcome: string;
	undetermined_reason: string | null;
	detail: string | null;
	persona: string | null;
	scorecard: {
		card: {
			dims?: { action: boolean | null; communicate: boolean | null; nl: boolean | null };
			failed_dims?: string[];
			nl?: {
				results?: { assertion: string; met: boolean | null; na?: boolean; reason?: string }[];
			};
			action?: { missing?: string[]; forbidden_called?: string[] };
			/** 确定性检查逐条（EV-25）：dim＝并进哪一维（action ／ communicate），null＝未机判 */
			deterministic?: {
				results?: {
					check: string;
					dim: "action" | "communicate" | null;
					met: boolean | null;
					detail?: string;
				}[];
			};
		} | null;
		items: {
			gold_item_id: string;
			outcome_expected: string | null;
			outcome_actual: string | null;
			pass: boolean | null;
			activated: boolean;
		}[];
		derived_terminal: {
			write_object_errors?: number;
			forbidden_commits?: number;
			duplicate_commits?: number;
		};
	};
	session_output: {
		termination_reason: string | null;
		num_turns: number | null;
		false_claims: number | null;
	};
	record_ids: string[];
	cost_usd: number | null;
};
export function usePlanTrial(trialId: string | null) {
	return useQuery<PlanTrialDetail>({
		queryKey: ["plan-lab", "eval-trial", trialId],
		enabled: !!trialId,
		retry: false,
		queryFn: async () => {
			const { data } = await apiClient.get<PlanTrialDetail>(
				`/admin/v1/plan-lab/eval/trials/${encodeURIComponent(trialId ?? "")}`,
			);
			return data;
		},
	});
}

/** 方法沉淀（原「优化打法」）：仓库 `.claude/skills/<名>/SKILL.md` 的说明、版本历史与全文（只读，变更走 PR） */
export type PlanMethodSkillUpdate = { date: string; change: string; source: string };
export type PlanMethodSkill = {
	name: string;
	/** 范围：plan ／ common（本页只列这两种；loop-* 在 Loop 页，前端规范两边都不列） */
	scope?: "plan" | "common" | "loop" | "dev";
	description: string;
	body: string;
	updates: PlanMethodSkillUpdate[];
	latest_update: string | null;
	versions: number;
	path: string;
};

/** 评测报告（docs/plan-eval-reports/*.md，只读同源） */
export type PlanEvalReport = {
	name: string;
	title: string;
	batch_id: string;
	compare: string;
	date: string;
	pass_rate: string;
	summary: string;
	body: string;
	updates: PlanMethodSkillUpdate[];
	latest_update: string | null;
	versions: number;
	path: string;
	/** 渲染时换掉的盲测题段落数（10-21 A1；历史 md 不改） */
	sealed_redacted?: number;
};

export function usePlanEvalReports() {
	return useQuery<{ reports: PlanEvalReport[] }>({
		queryKey: ["plan-lab", "eval-reports"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ reports: PlanEvalReport[] }>(
				"/admin/v1/plan-lab/eval-reports",
			);
			return data;
		},
	});
}

export function usePlanMethodSkills() {
	return useQuery<{ skills: PlanMethodSkill[] }>({
		queryKey: ["plan-lab", "method-skills"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ skills: PlanMethodSkill[] }>(
				"/admin/v1/plan-lab/method-skills",
			);
			return data;
		},
	});
}

/* ── 运行规则（执行护栏页签）：运行时代码规则注册表，只读 ── */

/** 层表项（专题《维护对象边界定义》）：id 即 layer 的取值 */
export type PlanLayer = { id: string; name: string; desc: string };

export type PlanRule = {
	id: string;
	stage: string;
	name: string;
	does: string;
	kind: string;
	/** 归属的通用机制编号（M1～M16）；空＝独立规则（基线升级第 5 批） */
	mechanism?: string;
	depends: string;
	config: string;
	where: string;
	trace: string;
	/** 适用范围（批 5a）：general 通用 ／ ecommerce 电商 ／ platform 特定平台 ／ tool 特定工具 */
	scope?: string;
	/** 特定平台 ／ 特定工具时是哪个（平台键或工具名，可多个）；其余为空 */
	scope_target?: string[];
	/** 依赖方式：config 依赖配置 ／ hardcoded 写死在代码 ／ none 不依赖平台 */
	binding?: string;
	/** 写死项：写死了什么、在哪、建议挪到哪（pack ／ tool_registry ／ field_contract ／ code）、说明 */
	hardcoded?: PlanRuleHardcoded[];
	/** 可配的规则：当前生效快照里的值；不可配为 null */
	config_value: unknown;
	/** 读了字段契约里哪些字段（对象.字段 ／ 工具.字段 ／ *.字段；批 5b） */
	fields?: string[];
	/** 读了业务包哪些参数（业务参数页登记的位置，如 platform.order_match；批 5b） */
	params?: string[];
	/** 层：L1 ／ L2-行业 ／ L2-平台 ／ L2-语言 ／ L3 ／ 环境（管理标注，不影响运行） */
	layer?: string;
	/** 混合时：哪部分该拆到哪 */
	layer_notes?: string;
	/** 变更史（规则定义卡只留定义，带题号 ／ 日期的历史叙述放这里；O-07） */
	history?: string;
};

export type PlanRuleHardcoded = { what: string; where: string; extract_to: string; note: string };

/** 规则标签的一个取值（适用范围 ／ 依赖方式 ／ 建议挪到哪）：键、中文名、说明 */
export type PlanRuleTag = { id: string; name: string; desc: string };

export function usePlanRules() {
	return useQuery<{
		stages: string[];
		kinds: string[];
		snapshot_id: string | null;
		rules: PlanRule[];
		mechanisms?: { id: string; name: string; does: string }[];
		scopes?: PlanRuleTag[];
		bindings?: PlanRuleTag[];
		extract_to?: PlanRuleTag[];
		/** 平台键 ／ skillport 的中文名；工具名原样显示 */
		target_labels?: Record<string, string>;
		/** 层表（取值与说明）；旧接口没有 */
		layers?: PlanLayer[];
	}>({
		queryKey: ["plan-lab", "rules"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/rules");
			return data;
		},
	});
}

/* ── 知识（应答配置 › 知识）：店铺知识条目，只读 ── */

export type PlanKnowledgeEntry = {
	id: string;
	title: string;
	content: string;
	/** 旧键（批 4 前：意图含诉求标签才推） */
	intents?: string[];
	/** 意图标签（批 4 起：只加分，推送按相关性判断） */
	intent_tags?: string[];
	data_states?: string[];
	/** 适用店铺；空＝全部店 */
	stores?: string[];
	keywords?: string[];
	source?: string;
	status?: string;
	/** 类型（标签字典「内容分类 › 知识类型」的键，批 5）：只用于管理、筛选、统计 */
	kind?: string;
	/** 约束类条目：约束哪些写工具、在什么数据状态下约束（没写取条目的）、什么情况例外（执行前把关）；
	 * 例外引用业务包取值表里的类（{class: "quality"}），旧条目直接列取值 */
	constraint?: {
		tools?: string[];
		data_states?: string[];
		unless?: Record<string, string[] | { class: string }>;
	};
	/** 层与混合说明（管理标注，不影响运行） */
	layer?: string;
	layer_notes?: string;
};

/** 知识类型取值（业务包 knowledge.json · kinds；模板预置，客户可加） */
export type PlanKnowledgeKind = {
	key: string;
	name: string;
	desc?: string;
	origin?: "template" | "customer";
};

/* ── 字段契约（应答配置 › 字段契约）：工具返回字段 × 环节，只读 ── */

export type FieldContractRow = {
	scope: string;
	key: string;
	field: string;
	desc: string;
	to: string[];
	object?: string | null;
	sensitive?: boolean;
	/** 个人信息（收货地址、姓名、付款记录）：照常给模型，只是标出来；和「敏感」不同（敏感＝不给模型） */
	pii?: boolean;
	/** 角色：代码按角色取字段、不认字段名（批 5b） */
	role?: string;
	/** 读这个字段的运行规则（规则注册表 RULE_FIELDS 反查；批 5b） */
	rules?: string[];
	/** 由它派生的数据状态（批 5b） */
	states?: string[];
	note?: string;
	n2_only?: string | null;
	/** 不给 N2 的原因（写了就不报「N2 看不到」告警，FAC-N2-GAP） */
	n2_skip?: string;
	/** 可作「办不了」依据的原因类（cant_basis，G10 按它认；not_found 查无 ／ precondition 前置不满足） */
	cant_basis?: string;
};

export function usePlanFieldContract() {
	return useQuery<{
		consumers: Record<string, string>;
		rows: FieldContractRow[];
		/** 角色 → 说明 */
		roles?: Record<string, string>;
		说明: string;
	}>({
		queryKey: ["plan-lab", "field-contract"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/field-contract");
			return data;
		},
	});
}

/* ── 业务参数（业务配置 › 业务参数，批 5b）：业务包里的业务参数与平台口径，只读 ── */

export type BusinessParam = {
	/** 在业务包 pack.json 里的位置（点分） */
	path: string;
	/** 分组：business 业务参数 ／ platform 平台口径 ／ write 写操作默认值 */
	group: string;
	name: string;
	meaning: string;
	/** 读它的运行规则编号 */
	rules: string[];
	/** 还在哪用（知识正文、数据状态…） */
	used: string;
	/** 当前值（业务包现读；对象已去掉「说明」） */
	value: unknown;
	/** 业务包里这一节的说明（对象型参数才有） */
	note: string;
};

export type BusinessParamsResponse = {
	pack: string;
	说明: string;
	platform_note: string;
	groups: { id: string; name: string; desc: string }[];
	params: BusinessParam[];
	/** 业务包里不在本页展示的节 → 在哪个页面看（页签键；— ＝ 不展示） */
	elsewhere: Record<string, string>;
};

export function usePlanBusinessParams() {
	return useQuery<BusinessParamsResponse>({
		queryKey: ["plan-lab", "business-params"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/business-params");
			return data;
		},
	});
}

/* ── 信息流（应答配置 › 信息流）：大模型节点输入块登记，只读 ── */

export type InfoFlowRow = {
	node: string;
	key: string;
	sub: string;
	what: string;
	source: string;
	builder: string;
	when: string;
	guard: string;
	contract: string;
	step: string;
	gap: string;
};

export function usePlanInfoFlow() {
	return useQuery<{ nodes: Record<string, string>; rows: InfoFlowRow[] }>({
		queryKey: ["plan-lab", "info-flow"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/info-flow");
			return data;
		},
	});
}

export type OnboardingRow = {
	tool: string;
	item: string;
	desc: string;
	ok: boolean | null;
	note: string;
};

export function usePlanToolsOnboarding() {
	return useQuery<{
		tools: number;
		passed: number;
		failed: string[];
		rows: OnboardingRow[];
		items: { item: string; desc: string; manual: boolean }[];
		evidence_kinds: Record<string, string>;
	}>({
		queryKey: ["plan-lab", "tools", "onboarding"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/tools/onboarding");
			return data;
		},
	});
}

/* ── 工具能力（应答配置 › 工具能力）：plan 在用的工具清单 ＋ 演示数据试调用，只读 ── */

export type PlanToolProfile = {
	/** 意图标签（画像 pv4 起只加分，不作召回路） */
	intents: string[];
	standing: boolean;
	risk: string;
	prerequisites: string[];
	/** basis（画像 pv14 起）：objective 客观做不到；window 跟业务包默认可办窗口 platform.write_windows 走 */
	preconditions: { state: string; reason: string; basis?: "objective" | "window" }[];
	confusable: string[];
	/** 适用数据状态：画像 pv4 起删了（只留执行前置），旧画像照旧给 */
	data_states: string[];
	produces: string[];
	/** 客户常见说法：画像 pv4 起并进「何时用」末尾的例句（见 examples），旧画像照旧给 */
	phrases: string[];
	/** 「何时用」里「客户常这么说」的例句（pv4 起）；旧接口没有时按空 */
	examples?: string[];
	usage: string;
	/** 层与混合说明（管理标注，不影响运行） */
	layer?: string;
	layer_notes?: string;
	what: string;
	when: string;
	when_not: string;
	/** when_not 里「能力边界」那段；没有为空 */
	boundary: string;
	needs_provides: string;
};

export type PlanToolReturnField = {
	field: string;
	desc: string;
	to: string[];
	object: string | null;
	object_desc: string | null;
	object_fields: number;
	list: boolean;
	sensitive: boolean;
	/** 个人信息：进模型但标出来（收货地址、姓名、付款记录） */
	pii?: boolean;
	note: string;
};

export type PlanTool = {
	name: string;
	type: "READ" | "WRITE" | "GENERIC" | string;
	role: string;
	n2_desc: string;
	n2_usage: string;
	/** N2 可在 call_tool 那一行直接带的入参（注册表 n2_params，EXE-N2-PARAMS） */
	n2_params?: { key: string; path: string; note: string }[];
	required: string[];
	input: { name: string; required: boolean; note: string }[];
	sample_input: Record<string, unknown>;
	logic: string;
	lines: [string, string] | null;
	confirm_gate: boolean;
	select: { kind: string; label: string; from: string }[];
	backend: string;
	profile: PlanToolProfile | null;
	returns: PlanToolReturnField[];
};

/** 能力清单一行（#678）：系统能做的动作，由工具注册表 ＋ 工具说明 ＋ 店铺知识约束生成 */
export type PlanCapability = {
	tool_id: string;
	/** write 写 ／ read 只读 ／ handoff 转人工 */
	kind: "write" | "read" | "handoff" | string;
	action: string;
	what: string;
	says: string[];
	objects: string[];
	/** 在默认店铺能不能用（写动作被店铺知识整条拦掉就是 false） */
	available: boolean;
};

export function usePlanTools() {
	return useQuery<{
		profile_version: string;
		/** 召回口径：relevance＝相关性为主、意图只加分（pv4 起）；旧接口没有 */
		recall_mode?: "relevance" | "routes";
		backends: string[];
		consumers: Record<string, string>;
		intents: Record<string, string>;
		/** 层表；旧接口没有 */
		layers?: PlanLayer[];
		tools: PlanTool[];
		/** 能力清单（#678）；旧接口没有 */
		capabilities?: { store: string; store_name: string; rows: PlanCapability[] };
	}>({
		queryKey: ["plan-lab", "tools", "list"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/tools");
			return data;
		},
	});
}

export type PlanToolSample = {
	tool: string;
	backend: "fixture";
	write: boolean;
	params: Record<string, unknown>;
	result: Record<string, unknown>;
};

/** 用演示数据试调用（只读 GET；后端强制走演示数据、写工具只预演） */
export function usePlanToolSample() {
	return useMutation({
		mutationFn: async (name: string) => {
			const { data } = await apiClient.get<PlanToolSample>(
				`/admin/v1/plan-lab/tools/${encodeURIComponent(name)}/sample`,
			);
			return data;
		},
	});
}

export type InfoFlowAlert = { node: string; key: string; count: number; last_seen: string };

export function usePlanInfoFlowAlerts(days = 3) {
	return useQuery<{ days: number; rows: InfoFlowAlert[] }>({
		queryKey: ["plan-lab", "info-flow", "alerts", days],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/info-flow/alerts", {
				params: { days },
			});
			return data;
		},
	});
}

export type FieldContractAlert = {
	tool: string;
	field: string;
	count: number;
	last_seen: string;
	/** N2 看不到告警才有：这个字段登记给了哪些环节 */
	to?: string[];
};

export function usePlanFieldContractAlerts(days = 3) {
	/** rows：未登记字段（FAC-CONTRACT）；n2_hidden：给了 N3 ／ 信号却没给 N2 的字段（FAC-N2-GAP） */
	return useQuery<{ days: number; rows: FieldContractAlert[]; n2_hidden?: FieldContractAlert[] }>({
		queryKey: ["plan-lab", "field-contract", "alerts", days],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/field-contract/alerts", {
				params: { days },
			});
			return data;
		},
	});
}

export function usePlanKnowledge() {
	return useQuery<{
		/** 推送口径：relevance＝准入 → 订单状态 → 相关度 ＋ 意图标签加分（画像 pv5 起）；intent＝意图含诉求标签才推；旧接口没有时按 intent */
		push_mode?: "relevance" | "intent";
		intents: Record<string, string>;
		stores?: Record<string, string>;
		/** 知识类型取值表（批 5）；旧接口没有 */
		kinds?: PlanKnowledgeKind[];
		/** 层表；旧接口没有 */
		layers?: PlanLayer[];
		entries: PlanKnowledgeEntry[];
	}>({
		queryKey: ["plan-lab", "knowledge"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/knowledge");
			return data;
		},
	});
}

/** 应答指南（应答配置 › 应答指南，`GET /guidance`）：生效画像版本里的一条指南 ＋ 注入预览（后端用运行时渲染函数出，空串＝不注入该节点） */
export type PlanGuide = RecallGuide & {
	preview: { n2: string; n3: string };
};

export function usePlanGuidance() {
	return useQuery<{
		version: string;
		/** 召回口径：relevance＝相关性判断、标签只加分（画像 pv5 起，标签体系 v2 批 4）；intent＝按意图 ／ 工具挂；旧接口没有时按 intent */
		guide_mode?: "relevance" | "intent";
		/** 情绪与风险标签显示名（平台内置）；旧接口没有 */
		signal_names?: Record<"sentiment" | "risk_flags", Record<string, string>>;
		intents: Record<string, string>;
		/** 层表；旧接口没有 */
		layers?: PlanLayer[];
		guides: PlanGuide[];
	}>({
		queryKey: ["plan-lab", "guidance"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/guidance");
			return data;
		},
	});
}

/* ── P1 召回：召回管理（应答配置 › 召回）与召回评测（评测模式 › 召回评测）；方案 v1.0 §六，判定卡 11 ／ 12 ── */

export type RecallPrecondition = { state: string; reason?: string; basis?: "objective" | "window" };
export type RecallTool = {
	tool_id: string;
	risk?: string;
	standing?: boolean;
	/** 只有旧画像 pv2 有（批 2 删，要不要绑订单改看工具注册表 binds） */
	domain?: string[];
	/** 适用意图（旧画像 pv3 及更早：意图表的键；诉求标签命中即召回，10-16 意图路） */
	intents?: string[];
	/** 意图标签（画像 pv4 起：只给相关性加分，不命中不剔除；标签体系 v2 批 3） */
	intent_tags?: string[];
	/** 业务对象（pv3 起只有订单、行项、商品、优惠码） */
	entities?: string[];
	/** 信息项（批 2：原因、件数、金额、商品属性、问题、地址从实体挪来；pv4 起只用于组参，不作召回路） */
	slots?: string[];
	/** 适用数据状态（旧画像的数据路；pv4 起删了，只留执行前置 preconditions） */
	data_states?: string[];
	preconditions?: RecallPrecondition[];
	prerequisites?: string[];
	produces?: string[];
	confusable?: string[];
	desc?: { what?: string; when?: string; when_not?: string; needs_provides?: string };
	/** 客户常见说法（旧画像的说法路；pv4 起并进 desc.when 末尾「客户常这么说」） */
	phrases?: string[];
	[k: string]: unknown;
};
export type RecallGuide = {
	id: string;
	title?: string;
	condition_text?: string;
	action?: string;
	applies_to_tools?: string[];
	entity_conds?: { entity: string; present: boolean }[];
	data_conds?: string[];
	dialogue_conds?: string[];
	semantic?: string | null;
	node?: string;
	strength?: string;
	mode?: string;
	standing?: boolean;
	status?: string;
	/** 挂到哪些诉求意图（画像 pv4 及更早：写了就按意图挂，不再看 applies_to_tools；基线升级 B2） */
	intents?: string[];
	/** 意图标签（画像 pv5 起：只给相关性加分，不命中不剔除；标签体系 v2 批 4） */
	intent_tags?: string[];
	/** 情绪与风险标签（画像 pv5 起：本轮信号命中只加分，不硬路由；平台内置取值） */
	signal_tags?: { sentiment?: string[]; risk_flags?: string[] };
	/** 信息项条件：in ＝已知且取值在列表里才召回；not_in ＝已知且在列表里就排除；present ＝要求已知 ／ 未知（批 2） */
	/** class ／ not_class：引用业务包取值表 info_values 里的类（如退货原因的质量类），不抄取值（pv15 起） */
	slot_conds?: {
		slot: string;
		in?: string[];
		not_in?: string[];
		class?: string;
		not_class?: string;
		present?: boolean;
	}[];
	/** 出处说明：只留定义（pv15 起）；带日期 ／ 题号的变更史在 history，只进后台 */
	rule_note?: string;
	history?: string;
	/** 层与混合说明（管理标注，不影响运行） */
	layer?: string;
	layer_notes?: string;
	[k: string]: unknown;
};
export type RecallProfileVersion = {
	version: string;
	saved_at: string | null;
	operator: string;
	note: string;
	base: string | null;
	active: boolean;
};
export type RecallProfile = {
	version: string;
	active: boolean;
	/** 工具召回口径：relevance＝相关性为主、意图加分（pv4 起）；routes＝四路（pv3 及更早）；旧接口没有时按 routes */
	recall_mode?: "relevance" | "routes";
	/** 指南 ／ 知识召回口径：relevance＝相关性判断、标签只加分（pv5 起，批 4）；intent＝按意图 ／ 工具挂；旧接口没有时按 intent */
	guide_mode?: "relevance" | "intent";
	/** 指南可挂的情绪与风险标签（平台内置）；旧接口没有 */
	signal_tags?: Record<"sentiment" | "risk_flags", { key: string; name: string }[]>;
	entity_types: string[];
	data_states: string[];
	/** 信息项键（业务包 slot_keys；批 2 起工具可挂 slots）；旧接口没有时按空 */
	slot_keys?: string[];
	/** 工具可挂的意图（业务包意图表）；旧接口没有时按空 */
	intents?: { key: string; name: string }[];
	dialogue_states: string[];
	tools: RecallTool[];
	guides: RecallGuide[];
};
export type RecallRegression = {
	gold: string;
	split: string;
	/** 回归跑哪条臂（收尾 B6 起 jev：与运行时同一条路） */
	arm?: string;
	jev_model?: string;
	decision_points: number;
	/** 底版本 ＋ 草稿两遍的 Jev 调用数、失败数（有失败判不出、不放行）、耗时秒 */
	jev_calls?: number;
	jev_failed?: number;
	seconds?: number;
	rows: { metric: string; before: number | null; after: number | null; ok: boolean }[];
	passed: boolean;
};
export type RecallMetrics = {
	tool_recall: number | null;
	tool_precision: number | null;
	top1: number | null;
	/** 推荐档命中（可接受工具在推荐档；批 3 起，旧回放没有） */
	rec_hit?: number | null;
	/** 每件诉求平均候选数 ／ 推荐档个数（不含常备；批 3 起） */
	avg_candidates?: number | null;
	avg_recommended?: number | null;
	cannot_do_hit: number | null;
	guide_recall: number | null;
	guide_false_recall: number | null;
	/** 知识推送召回 ／ 误推率（对知识推送金标 kn-v1.1，批 4 起 Jev 臂才有） */
	knowledge_push_recall?: number | null;
	knowledge_false_push?: number | null;
};
export type RecallRunSummary = {
	run_id: string;
	created_at: string;
	status: "running" | "done" | "failed";
	gold: string;
	profile_version: string;
	arm: string;
	feed: string;
	split: string | null;
	progress: { done: number; total: number };
	error: string | null;
	metrics: RecallMetrics;
};
export type RecallBadCase = {
	dp_id: string;
	gold_item: string;
	goal: string | null;
	acceptable: string[];
	top: string[];
	miss: boolean;
};
export type RecallReport = RecallMetrics & {
	decision_points: number;
	top_n: number;
	cannot_do_false: number;
	by_split: Record<string, RecallMetrics & { decision_points: number }>;
	confusion: { gold: string; got: string; n: number }[];
	bad_cases: RecallBadCase[];
	signal?: {
		dp: number;
		count_match: number;
		multi_gold: number;
		multi_hit: number;
	};
	/** 知识推送（对知识推送金标 kn-v1.1；批 4 起 Jev 臂才有） */
	knowledge?: {
		push_recall: number | null;
		false_push: number | null;
		avg_pushed: number | null;
		counts: Record<string, number>;
	};
};
export type RecallRun = Omit<RecallRunSummary, "metrics"> & { report?: RecallReport };
export type RecallCandidate = {
	tool_id: string;
	/** 四路口径（pv3 及更早）命中了哪几路 */
	routes?: string[];
	/** 相关性口径（pv4 起）：怎么进的候选（all 全量 ／ prefilter 粗筛 ／ prerequisite 前置 ／ standing 常备） */
	entry?: string;
	/** 诉求意图在不在工具意图标签里（只加分） */
	intent_tag?: boolean;
	/** 相关度（Jev 概率；大模型判是 ／ 否 ／ 拿不准折成 0.8 ／ 0.2 ／ 0.5）；没判到为 null */
	relevance?: number | null;
	/** 意图加分 */
	intent_bonus?: number;
	/** 最终分＝相关度＋意图加分（封顶 1） */
	score?: number | null;
	/** 为什么在这档 */
	tier_why?: string;
	prerequisite_of?: string[];
	tier?: string;
	fit?: string | null;
	reason?: string | null;
	why?: string | null;
	rank?: number;
	grade?: string;
};
export type RecallDp = {
	dp_id: string;
	split: string | null;
	message: string | null;
	gold_demands: {
		gold_item: string;
		goal: string | null;
		entities: { type: string; value: string }[];
		acceptable_tools: string[];
		acceptable_pre: string[];
		cannot_do: boolean;
	}[];
	gold_guides: Record<string, "recall" | "not_recall">;
	result: {
		demands?: {
			gold_item: string;
			candidates: RecallCandidate[];
			conclusion?: string;
			admission_blocked?: unknown[];
		}[];
		guides?: string[];
		data_states?: string[];
		judged?: unknown;
		/** 指南进来 ／ 没进来的原因（pv5 起，按金标事项号） */
		guide_trace?: Record<string, RecallGuideTrace>;
		/** 推送的知识条目（pv5 起 Jev 臂，按金标事项号） */
		knowledge?: Record<string, string[]>;
	} | null;
	/** 知识推送金标（批 4）：{金标事项号: {条目: push ／ not_push ／ unsure}}；旧接口没有 */
	gold_knowledge?: Record<string, Record<string, "push" | "not_push" | "unsure">>;
};

const RB = "/admin/v1/plan-lab/recall";

/** 候选召回页的召回结构（`GET /recall/schema`，只读）：数据状态、排序按引擎、呈现分层、指南来源、知识推送 */
export type RecallStateRow = {
	key: string;
	scope: string;
	fields: string[];
	how: string;
	rules: string[];
	/** 在画像枚举里（工具、指南能选它） */
	in_enum: boolean;
	/** 取数这一层会算出它 */
	produced: boolean;
	used_by: { recall: string[]; admission: string[]; knowledge: string[]; guides: string[] };
};
export type RecallGuideSourceKey = "by_relevance" | "by_facts" | "standing" | "idle";
/** 指南 ／ 知识进来 ／ 没进来的原因（召回留痕 guide_trace，画像 pv5 起） */
export type RecallGuideTraceRow = {
	guide_id: string;
	why: string;
	relevance?: number | null;
	score?: number | null;
	pending?: string[];
	bonus?: Record<string, number>;
	signal_hit?: string[];
};
export type RecallGuideTrace = {
	judged: boolean;
	in: RecallGuideTraceRow[];
	out: RecallGuideTraceRow[];
};
export type RecallSchema = {
	profile_version: string;
	data_states: RecallStateRow[];
	state_use_rules: Record<"recall" | "admission" | "knowledge" | "guides", string[]>;
	/** 退货窗口：默认店铺的天数 ＋ 有窗口的店（编号与名称，业务包 stores／店铺参数） */
	window: { days: number; stores: string[]; store_names?: string[] };
	rank: {
		engine: string;
		reason_codes: string[];
		tiers: string[];
		rules: string[];
		jev?: {
			requested_model: string;
			/** 版本与分档线的配置键（收尾 B1 起进配置快照） */
			model_key?: string;
			threshold_keys?: string[];
			locked: boolean;
			seen_model: string | null;
			seen_source: "process" | "ledger" | null;
			seen_at: string | null;
			yes_at: number;
			no_at: number;
			timeout_s: number;
			question: string;
			question_key: string;
		};
		llm?: { model: string | null; model_key: string; system: string; user: string };
	};
	focus: { standing_tools: string[]; rules: string[] };
	/** 工具候选怎么来（标签体系 v2 批 3）；旧接口没有 */
	tool_recall?: {
		mode: "relevance" | "routes";
		non_standing: number;
		full_judge_max: number;
		full_judge_max_key: string;
		prefilter: boolean;
		intent_bonus: number;
		intent_bonus_key: string;
		yes_at: number;
		no_at: number;
		rules: string[];
	};
	/** 指南 ／ 知识怎么来（标签体系 v2 批 4，画像 pv5 起）；旧接口没有 */
	guide_recall?: {
		mode: "relevance" | "intent";
		guide_min: number;
		guide_intent_bonus: number;
		signal_bonus: number;
		know_min: number;
		know_intent_bonus: number;
		judge_max: number;
		keys: Record<string, string>;
		guide_question: string;
		knowledge_question: string;
		question_keys: string[];
		rules: string[];
	};
	guides: {
		/** relevance＝相关性判断（pv5 起）；intent＝按意图 ／ 工具挂；旧接口没有 */
		mode?: "relevance" | "intent";
		total_live: number;
		idle_intent: string;
		idle_intent_name: string;
		sources: { key: RecallGuideSourceKey; guides: string[]; rules: string[] }[];
	};
	knowledge: {
		total: number;
		with_states: string[];
		constraints: {
			id: string;
			title: string;
			tools: string[];
			unless: Record<string, string[]>;
			data_states: string[];
		}[];
		search: { jev_min: number; top: number };
		rules: string[];
	};
	rule_names: Record<string, string>;
};

export function useRecallSchema() {
	return useQuery<RecallSchema>({
		queryKey: ["plan-lab", "recall", "schema"],
		queryFn: async () => (await apiClient.get(`${RB}/schema`)).data,
	});
}

export function useRecallProfiles() {
	return useQuery<{ active: string; versions: RecallProfileVersion[] }>({
		queryKey: ["plan-lab", "recall", "profiles"],
		queryFn: async () => (await apiClient.get(`${RB}/profiles`)).data,
	});
}

export function useRecallProfile(version: string | undefined) {
	return useQuery<RecallProfile>({
		queryKey: ["plan-lab", "recall", "profile", version],
		queryFn: async () =>
			(await apiClient.get(`${RB}/profiles/${encodeURIComponent(version ?? "")}`)).data,
		enabled: !!version,
	});
}

export type RecallDraftBody = {
	base_version: string;
	tools?: RecallTool[];
	guides?: RecallGuide[];
};

export function useRecallRegression() {
	return useMutation({
		mutationFn: async (body: RecallDraftBody) =>
			(
				await apiClient.post<RecallRegression>(`${RB}/profiles/regression`, body, {
					timeout: 300_000, // Jev 臂约 75 次调用，要等一会儿（收尾 B6）
				})
			).data,
	});
}

export function useRecallSaveProfile() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: RecallDraftBody & { note: string }) =>
			(
				await apiClient.post<RecallProfileVersion>(
					`${RB}/profiles`,
					{ ...body, operator: "admin-ui" },
					{ timeout: 300_000 }, // 保存前再核一次回归（同一份草稿刚跑过走缓存）
				)
			).data,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["plan-lab", "recall"] });
			// 画像版本写进了新配置快照（收尾 B1）
			qc.invalidateQueries({ queryKey: ["plan-lab", "config"] });
			qc.invalidateQueries({ queryKey: ["plan-lab", "config-snapshots"] });
			qc.invalidateQueries({ queryKey: ["plan-lab", "config-run-env"] });
		},
	});
}

export function useRecallGold() {
	return useQuery<{ versions: string[] }>({
		queryKey: ["plan-lab", "recall", "gold"],
		queryFn: async () => (await apiClient.get(`${RB}/gold`)).data,
	});
}

export function useRecallEstimate(p: { gold?: string; arm: string; feed: string; split: string }) {
	return useQuery<{
		decision_points: number;
		llm_calls: number;
		est_seconds: number;
		est_usd: number;
	}>({
		queryKey: ["plan-lab", "recall", "estimate", p],
		queryFn: async () =>
			(
				await apiClient.get(`${RB}/runs/estimate`, {
					params: { ...p, split: p.split || undefined },
				})
			).data,
		enabled: !!p.gold,
	});
}

export function useRecallRunCreate() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: {
			gold: string;
			profile_version?: string;
			arm: string;
			feed: string;
			split?: string | null;
		}) => (await apiClient.post<RecallRunSummary>(`${RB}/runs`, body)).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: ["plan-lab", "recall", "runs"] }),
	});
}

export function useRecallRuns() {
	return useQuery<{ rows: RecallRunSummary[]; total: number }>({
		queryKey: ["plan-lab", "recall", "runs"],
		queryFn: async () => (await apiClient.get(`${RB}/runs`)).data,
		refetchInterval: (q) => (q.state.data?.rows.some((r) => r.status === "running") ? 3000 : false),
	});
}

export function useRecallRun(runId: string | undefined) {
	return useQuery<RecallRun>({
		queryKey: ["plan-lab", "recall", "run", runId],
		queryFn: async () =>
			(await apiClient.get(`${RB}/runs/${encodeURIComponent(runId ?? "")}`)).data,
		enabled: !!runId,
		refetchInterval: (q) => (q.state.data?.status === "running" ? 3000 : false),
	});
}

export function useRecallDp(runId: string | undefined, dpId: string | undefined) {
	return useQuery<RecallDp>({
		queryKey: ["plan-lab", "recall", "dp", runId, dpId],
		queryFn: async () =>
			(
				await apiClient.get(
					`${RB}/runs/${encodeURIComponent(runId ?? "")}/dps/${encodeURIComponent(dpId ?? "")}`,
				)
			).data,
		enabled: !!runId && !!dpId,
	});
}

/* ── 信号识别（应答配置 › 信号识别）：意图表、实体与信息项、规则层与确认门、最近信号留痕，只读 ── */

export type PlanSignalIntent = {
	key: string;
	name: string;
	desc: string;
	/** false ＝ 不建事项（如闲聊）；意图表 creates_item */
	creates_item: boolean;
	/** 一级（标签字典批 1） */
	parent?: string;
	parent_name?: string;
	/** 生效画像里挂了这个意图的工具 */
	tools: string[];
	/** 生效画像里挂了这个意图的生效指南 */
	guides: string[];
};

export type PlanSignalConfig = {
	profile_version: string;
	snapshot_id: string | null;
	intent_table: {
		version: string;
		domain: string;
		note: string;
		/** 一级（意图表 groups） */
		groups: { key: string; name: string }[];
		intents: PlanSignalIntent[];
	};
	/** 业务对象：读标签字典「业务对象」维度（标签体系 v2 批 2）＋ 核实后进事项的落法 */
	objects: {
		key: string;
		name: string;
		desc: string;
		/** 字典编号（跳 #tags?tag=） */
		code: string;
		to: string;
		rule_id: string;
	}[];
	/** 信息项：读标签字典「信息项」维度；from_entity ＝ 原为哪类实体（批 2 并进来） */
	slots: {
		key: string;
		desc: string;
		code: string;
		from_entity: string | null;
		to: string;
		rule_id: string;
	}[];
	/** 情绪与风险取值（标签字典 EMOTION_RISK，平台内置） */
	emotion_risk: PlanEmotionRiskGroup[];
	return_reasons: string[];
	/** 取值表带类（业务包 info_values）；旧接口没有这一项 */
	return_reason_values?: { value: string; name: string; classes: string[] }[];
	tiers: string[];
	resumes: string[];
	rule_layer: { order_no_pattern: string; product_id_pattern: string };
	gate: {
		refuse_patterns: string[];
		confirm_tier: string;
		confirm_min_confidence: number;
		correctable_slots: string[];
		steps: { verdict: string; rules: string[] }[];
		order_rule: string;
	};
	inputs: {
		node: string;
		key: string;
		sub: string;
		what: string;
		source: string;
		builder: string;
		when: string;
		guard: string;
		contract: string;
		step: string;
		gap: string;
	}[];
	/** 规则注册表里的信号相关规则（不带当前配置值） */
	rules: Omit<PlanRule, "config_value">[];
};

export type PlanEmotionRiskGroup = {
	key: "sentiment" | "risk_flags" | string;
	name: string;
	desc: string;
	/** one ＝ 选一个（情绪）；many ＝ 每旗单独判、可多个（风险旗） */
	pick: "one" | "many";
	values: { key: string; name: string; desc: string; code: string }[];
};

export function usePlanSignal() {
	return useQuery<PlanSignalConfig>({
		queryKey: ["plan-lab", "signal", "config"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/signal");
			return data;
		},
	});
}

/* ── 应答配置 › 标签字典（标签体系 v2 批 1；GET /tags，只读）── */

export type PlanTagSource = "platform" | "computed" | "custom";

export type PlanTagDimension = {
	id: string;
	name: string;
	/** 这个维度回答什么问题 */
	question: string;
	source: PlanTagSource;
	/** 出处：行业模板 ／ 客户新增；平台内置、系统计算的维度没有 */
	origin: "template" | "customer" | null;
	levels: 1 | 2;
	judge: "model" | "code" | "model_code";
	/** live 已上线 ／ reserved 留位（未上线） */
	status: "live" | "reserved";
	/** 取值从哪读（指向现有正本） */
	values_from: string;
	used_for: string;
	/** 叶子数 */
	count: number;
};

export type PlanTagRef = {
	kind: "tool" | "guide" | "knowledge" | "rule" | "intent" | "dimension";
	id: string;
	via: string;
};

export type PlanTagExplain = {
	definition: string;
	includes: string;
	excludes: string;
	examples: { text: string; from?: string }[];
};

export type PlanTag = {
	/** 稳定编号：维度.一级.二级 */
	code: string;
	key: string;
	/** 显示名；后端没有的（数据状态、业务对象等）页面按召回页中文表显示 */
	name: string | null;
	parent: string | null;
	level: 1 | 2;
	desc: string;
	explain: PlanTagExplain | null;
	/** published ／ never_produced（取数从不产出） */
	state: string;
	notes: string[];
	/** 被引用反查用的键（准入维度带「店铺: ／ 状态:」前缀，其余同 key） */
	ref_key: string;
	refs: PlanTagRef[];
	fields?: string[];
	rules?: string[];
	/** 数据状态的计算口径（facts/states.STATE_DOC，批 2） */
	how?: string;
	/** 信息项：原为哪类实体（批 2 并进来）；其余没有 */
	from_entity?: string | null;
	/** 信息项的用法标记（业务包 slot_roles，批 5b） */
	roles?: string[];
	/** 内容分类：出处（行业模板 ／ 客户新增，批 5） */
	origin?: "template" | "customer";
};

export type PlanTagHealth = {
	kind: string;
	dim: string;
	keys: string[];
	msg: string;
	refs: PlanTagRef[];
};

export type PlanTagDictionary = {
	intent_table: string;
	profile_version: string;
	/** 字典版本（批 5：维度注册表 ＋ 标签定义的内容指纹 td-…；配置快照保存时记下） */
	dict_version?: string;
	dimensions: PlanTagDimension[];
	tags: Record<string, PlanTag[]>;
	extra_keys: { key: string; kind: string; name: string; note: string; maps_to?: string }[];
	health: PlanTagHealth[];
	check_kinds: Record<string, string>;
	/** 各体检类别查哪些维度的口径说明（收尾 B5：系统计算类不报「没人引用」） */
	health_scope?: Record<string, string>;
};

export function usePlanTags() {
	return useQuery<PlanTagDictionary>({
		queryKey: ["plan-lab", "tags"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/tags");
			return data;
		},
	});
}

export type PlanSignalDemand = {
	demand_id: string;
	goal?: string;
	span?: string | null;
	entities?: { type: string; value: unknown }[];
	demand_type?: string | null;
	tier?: string;
	resume?: string | null;
	target_item?: string | null;
	confidence?: number | null;
	slots?: Record<string, unknown>;
	ents?: Record<string, unknown>;
	/** 等确认时：客户否定 ／ 改写了确认卡上的哪几项（每项一句；SIG-13 纠正）；老留痕没有 */
	rewrites?: string[];
	/** 等确认时：是不是在问这笔待确认操作的后果（SR-CONFIRM-Q 暂缓按它）；没给 → null */
	asks_pending?: boolean | null;
};

export type PlanSignalVerdict = {
	demand_id?: string;
	item_id?: string;
	verdict: string;
	[k: string]: unknown;
};

export type PlanSignalTurn = {
	trace_id: string;
	session_id: string;
	at: string;
	source: string | null;
	batch_id: string | null;
	scenario_id: string | null;
	turn_no: number | null;
	message: string;
	reply: string;
	first_turn: boolean;
	degraded: string | null;
	rule_hits: string[];
	rule_facts: Record<string, unknown>;
	pending: Record<string, unknown> | null;
	demands: PlanSignalDemand[];
	resume_action: string | null;
	target_item_id: string | null;
	verdicts: PlanSignalVerdict[];
	merged_into: Record<string, unknown>[];
	new_item_ids: string[];
	confirm_voided: boolean;
	unanswered: boolean;
	/** 本轮情绪（negative ／ neutral ／ positive）；老留痕没有 → null（标签体系 v2 批 2） */
	sentiment?: string | null;
	/** 本轮成立的风险旗；没有 → [] */
	risk_flags?: string[];
	/** 模型给了取值表外的值、被丢弃的（{field, value}） */
	emotion_risk_dropped?: { field: string; value: unknown }[];
};

export function usePlanSignalRecent(limit = 20) {
	return useQuery<{
		days: number;
		limit: number;
		rows: PlanSignalTurn[];
		skipped_sealed: number;
		truncated: boolean;
	}>({
		queryKey: ["plan-lab", "signal", "recent", limit],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/signal/recent", {
				params: { limit },
			});
			return data;
		},
	});
}

/* ── 事项看板（应答配置 › 事项看板）：事项字段、状态与流转、落账规则、预算与上限、最近事项快照，只读 ── */

export type PlanBoardRule = Omit<PlanRule, "config_value">;

export type PlanBoardSchema = {
	/** 层间契约里事项结构的版本（contracts.CONTRACT_VERSIONS.item） */
	item_contract_version: number;
	fields: {
		group: string;
		key: string;
		desc: string;
		/** new_item 骨架里的默认值；建事项时另加的键为 null */
		default: unknown;
		in_skeleton: boolean;
		in_contract: boolean;
	}[];
	object_fields: { key: string; desc: string; default: unknown }[];
	enums: Record<string, string[]>;
	/** 诉求显示名（业务包 demand_labels） */
	demand_labels: Record<string, string>;
	statuses: { key: string; label: string; desc: string; terminal: boolean }[];
	wait_kinds: { key: string; label: string; desc: string; in_enum: boolean }[];
	cant_reasons: { key: string; desc: string }[];
	transitions: { from: string | null; to: string; why: string; rules: string[] }[];
	transition_rule: string;
	progress_signature: string[];
	groups: { key: string; title: string; line: string; rules: string[] }[];
	/** 规则注册表 stage=事项板 ／ 结算 */
	rules: PlanBoardRule[];
	budget: {
		snapshot_id: string | null;
		rows: {
			key: string;
			label: string;
			value: unknown;
			default: unknown;
			/** 生效快照里有这一项（没有就按默认） */
			from_snapshot: boolean;
			rule_id: string | null;
			does: string;
			trace: string;
		}[];
		/** 写死的计数（机制 M9 下没挂配置的规则） */
		fixed: PlanBoardRule[];
	};
	/** item_event 种类 → 中文 */
	event_kinds: Record<string, string>;
};

export function usePlanBoardSchema() {
	return useQuery<PlanBoardSchema>({
		queryKey: ["plan-lab", "board", "schema"],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/board/schema");
			return data;
		},
	});
}

export type PlanBoardItem = {
	item_id: string;
	demand_type: string | null;
	object: Record<string, unknown>;
	status: string;
	status_reason: Record<string, unknown> | null;
	wait_kind: string | null;
	markers: string[];
	active: boolean;
	/** 本轮新建（激活轮次＝本轮） */
	new: boolean;
	/** 本轮有事项事件 */
	changed: boolean;
	/** 专门展示的状态（只给有值的；老留痕没有 → 空对象） */
	detail?: PlanBoardItemDetail;
};

export type PlanBoardItemDetail = {
	/** 这件事项哪个写工具被拦过、被谁拦（EXE-NOSWAP 的依据） */
	write_blocked?: { tool_id: string; by: string; why: string }[];
	/** 挂着的确认卡：办理范围、请客户确认时说的与要提交的是否一致、要不要重新确认 */
	confirm_card?: {
		tool_id: string;
		scope?: string | null;
		shown?: { ok?: boolean; missing?: string[]; conflict?: string[]; turn?: number } | null;
		reconfirm?: { why?: string; missing?: string[]; conflict?: string[] } | null;
		/** 同意的同一句带了追问、这次没提交（SR-CONFIRM-Q）：先答追问再请确认 */
		held?: { why?: string; questions?: string[]; turn?: number } | null;
	};
	/** 转人工的交接摘要（EXE-HANDOFF-SUMMARY）；reused_from＝本会话已在那件转过人工、并入那次不再调（EXE-HANDOFF-ONCE） */
	handoff?: {
		summary?: string;
		note?: string;
		reason?: string;
		called?: boolean;
		reused_from?: string;
	};
	/** 从哪件事项拆出来的（跨单拆分） */
	split_from?: string;
	/** 追问哪件已办完的事项新建的 */
	follow_up_of?: string;
	/** 从母事项带过来的只读步骤（办完 ／ 办不了可按步骤号引用） */
	inherited_steps?: { step: number; tool_id: string; from: string }[];
};

export type PlanBoardEvent = {
	at: string | null;
	item_id: string | null;
	layer: string | null;
	kind: string;
	attrs: Record<string, unknown>;
};

export type PlanBoardTurn = {
	trace_id: string;
	session_id: string;
	at: string;
	source: string | null;
	batch_id: string | null;
	scenario_id: string | null;
	turn_no: number | null;
	message: string;
	stop_reason: string | null;
	plan_version: number | null;
	asked_item_id: string | null;
	items: PlanBoardItem[];
	events: PlanBoardEvent[];
};

export type PlanBoardRecent = {
	days: number;
	limit: number;
	rows: PlanBoardTurn[];
	skipped_sealed: number;
	truncated: boolean;
};

export function usePlanBoardRecent(limit = 20) {
	return useQuery<PlanBoardRecent>({
		queryKey: ["plan-lab", "board", "recent", limit],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/plan-lab/board/recent", {
				params: { limit },
			});
			return data;
		},
	});
}
